import React, { useState } from "react";
import * as SubframeCore from "@subframe/core";
import { TaskCenterRow, TaskCenterFilter, TaskCenterStatusVariantMap, TaskCenterStatusMap } from "modules/Retention/types";
import { Badge, } from "subframe";
import { TicketStatus } from "subframe/components/TicketStatus";

import { TaskCenterTableSkeleton } from "./TaskCenterTableSkeleton";
import { ForwardEscalationDialog } from "../../EscalationWithAI/components/ForwardEscalationDialog";
import { SetReminderDialog } from "../../EscalationWithAI/components/SetReminderDialog";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCol,
  TableHeader,
  TableActionCell
} from "common/components/Table";
import { formatString } from "modules/Retention/utils/recognition";
import RETENTION_ROUTES, { getEmployeeProfileRoute } from "modules/Retention/constants/routes";
import { useNavigate } from "react-router-dom";
import { TagEscalationStatus } from "subframe/components/TagEscalationStatus";
import BranchWithMetadata from "modules/Retention/components/BranchWithMetadata/BranchWithMetadata";
import { getTimeAgo } from "utils/date";
import useUser from "hooks/userHooks";

export const SENTIMENT_SCORE_MAPPER = {
  "LOW_SENTIMENT_SCORE": "Low sentiment",
  "LOW_SENTIMENT_SCORE_FLAGGED_QUESTION": "Flagged question",
  "INCOMPLETE_SURVEY": "Incomplete survey"
}

// Color mapping for escalation follow-up tags
const getTagColor = (tag: string) => {
  switch (tag) {
    case "Voicemail Left":
      return "#eff6ff"; // Light blue - communication related
    case "Manager Action Needed":
      return "#FFDEF2"; // Light pink - action required
    case "Escalation Forwarded":
      return "#f3e8ff"; // Light purple - forwarding/delegation
    case "Other":
      return "#f5f5f5"; // Light gray - neutral
    default:
      return "#FFDEF2"; // Default light pink
  }
}

interface TaskCenterTableProps {
  rows: TaskCenterRow[];
  filters?: TaskCenterFilter;
  loading?: boolean;
}

export const TaskCenterTable: React.FC<TaskCenterTableProps> = ({ rows, loading = false }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [isForwardDialogOpen, setIsForwardDialogOpen] = useState(false);
  const [isReminderDialogOpen, setIsReminderDialogOpen] = useState(false);
  const [selectedEscalation, setSelectedEscalation] = useState<TaskCenterRow | null>(null);

  const handleEmployeeDetails = (employeeId: number) => {
    const profileRoute = getEmployeeProfileRoute(user?.agency_id);
    navigate(profileRoute.replace(":id", employeeId.toString()));
  };

  const handleForwardEscalation = (row: TaskCenterRow) => {
    setSelectedEscalation(row);
    setIsForwardDialogOpen(true);
  };

  const handleSetReminder = (row: TaskCenterRow) => {
    setSelectedEscalation(row);
    setIsReminderDialogOpen(true);
  };

  const handleForwardDialogClose = (open: boolean) => {
    setIsForwardDialogOpen(open);
    if (!open) {
      setSelectedEscalation(null);
    }
  };

  const handleReminderDialogClose = (open: boolean) => {
    setIsReminderDialogOpen(open);
    if (!open) {
      setSelectedEscalation(null);
    }
  };

  const handleForwardSuccess = () => {
    // The dialog will handle closing itself and refreshing data via React Query
    setSelectedEscalation(null);
  };

  const handleReminderSuccess = () => {
    // The dialog will handle closing itself and refreshing data via React Query
    setSelectedEscalation(null);
  };

  // Helper function to determine priority based on reminder data and escalation time
  const getPriorityData = (row: TaskCenterRow) => {
    // If there's reminder data, show reminder icon regardless of other status
    if (row.reminder_data) {
      return {
        variant: 'reminder' as const,
        icon: 'FeatherCalendar' as any,
        text: 'Reminder',
        color: 'text-warning'
      };
    }
    
    // If no reminder but escalation is overdue, show overdue icon
    if (row.priority === "overdue") {
      return {
        variant: 'overdue' as const,
        icon: 'FeatherAlertTriangle' as any,
        text: 'Overdue',
        color: 'text-error'
      };
    }
    
    return null;
  };

  const handleRowClick = (row: TaskCenterRow) => {
    navigate(
      RETENTION_ROUTES.ESCALATION_WITH_AI
        .replace(":id", row.employee_info.employee_id.toString())
        .replace(":surveyId", row.survey_ids)
        .replace(":escalationId", row.escalation_id)
    );

  };

  // Helper function to get sentiment-based styling

  if (loading) {
    return <TaskCenterTableSkeleton />;
  }

  return (
    <div className="w-full overflow-x-auto">
      <Table className="table-fixed w-full">
        <TableHead>
          <TableRow isHeader >
            <TableHeader className="w-auto"></TableHeader>
            <TableHeader className="w-auto">Status</TableHeader>
            <TableHeader className="w-auto">Employee</TableHeader>
            <TableHeader className="w-auto">Tenure</TableHeader>
            <TableHeader className="w-auto">Reason</TableHeader>
            <TableHeader className="w-auto">Assignee</TableHeader>
            <TableHeader className="w-auto">Time</TableHeader>
            <TableHeader className="w-auto">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const priority = getPriorityData(row);
            const assigneeData = row.assignee ? {
              name: row.assignee,
              icon: 'FeatherUser' as any,
              color: 'text-primary'
            } : {
              name: 'Unassigned',
              icon: 'FeatherUserX' as any,
              color: 'text-subtext-color'
            };

            return (
              <TableRow key={`${row.employee_info.employee_id}-${index}`} onClick={() => handleRowClick(row)} className="cursor-pointer hover:bg-gray-50">
                <TableCol>
                  {priority && (
                    <TicketStatus
                      icon={priority.icon}
                      text={priority.text}
                      variant={priority.variant}
                    />
                  )}
                </TableCol>
                <TableCol className="!pl-0">
                  <div className="w-fit whitespace-nowrap flex-shrink-0">
                    {row.status === 'in_progress' && row.tags && row.tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {row.tags.map((tag, tagIndex) => (
                          <Badge
                            key={tagIndex}
                            variant="neutral"
                            className="text-xs"
                            style={{ backgroundColor: getTagColor(tag) }}
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <TagEscalationStatus 
                        score={TaskCenterStatusMap[row.status as keyof typeof TaskCenterStatusMap]} 
                        variant={row.priority === 'overdue' ? 'alert-0' : TaskCenterStatusVariantMap[row.status as keyof typeof TaskCenterStatusVariantMap] as any} 
                      />
                    )}
                  </div>
                </TableCol>
                <TableCol>
                  <div className="flex min-h-[48px] flex-col items-start justify-center space-y-1 pointer-events-none">
                    <span className="text-body-bold font-body-bold text-default-font truncate max-w-full">
                      {row.employee_info.name}
                    </span>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-1 text-xs">
                      <span className="text-label font-label text-subtext-color truncate">
                        {row.employee_info.job_title}
                      </span>
                      <span className="hidden sm:inline text-label font-label text-subtext-color">,</span>
                      <BranchWithMetadata
                        name={row.employee_info.office_location}
                        overallSentiment={
                          row.office_location_data
                            ?.survey_sentiment_data?.["Overall Sentiment"]
                        }
                        responseRate={row.office_location_data
                          ?.survey_sentiment_data?.["Response Rate"]}
                      />
                      <span className="hidden sm:inline text-label font-label text-subtext-color">,</span>
                      <span className="text-label font-label text-subtext-color truncate">
                        {row.employee_info.region}
                      </span>
                    </div>
                  </div>
                </TableCol>
                <TableCol>
                  <span className="whitespace-nowrap text-body font-body text-default-font">
                    {row.tenure}
                  </span>
                </TableCol>
                <TableCol>
                  <div className="w-fit">
                    <Badge variant="neutral" className="text-xs">
                      {SENTIMENT_SCORE_MAPPER[row.escalation_reason as keyof typeof SENTIMENT_SCORE_MAPPER] || formatString(row.escalation_reason)}
                    </Badge>
                  </div>
                </TableCol>
                <TableCol>
                  <div className="flex items-center gap-1 pb-0.5 cursor-pointer">
                    <span className={`text-label-bold font-label-bold ${assigneeData.color} truncate`}>
                      {assigneeData.name}
                    </span>
                    <SubframeCore.Icon
                      className={`text-label font-label ${assigneeData.color} flex-shrink-0`}
                      name={assigneeData.icon}
                    />
                  </div>
                </TableCol>
                <TableCol>
                  <span className="text-label font-label text-default-font flex items-center whitespace-nowrap">
                    {getTimeAgo(row.escalation_created_at)}
                  </span>
                </TableCol>
                <TableActionCell
                  actions={[
                    {
                      icon: "FeatherFlagTriangleRight",
                      label: "View Escalation ticket",
                      onClick: (e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        handleRowClick(row)
                      }
                    },
                    {
                      icon: "FeatherUserCircle",
                      label: "View Employee details",
                      onClick: (e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        handleEmployeeDetails(row.employee_info.employee_id)
                      }
                    },
                    {
                      icon: "FeatherClock",
                      label: "Set reminder",
                      onClick: (e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        handleSetReminder(row)
                      }
                    },
                    {
                      icon: "FeatherSend",
                      label: "Forward escalation",
                      onClick: (e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        handleForwardEscalation(row)
                      }
                    },
                  ]}
                />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Forward Escalation Dialog */}
      {selectedEscalation && (
        <ForwardEscalationDialog
          open={isForwardDialogOpen}
          onOpenChange={handleForwardDialogClose}
          escalationId={parseInt(selectedEscalation.escalation_id)}
          employeeId={selectedEscalation.employee_info.employee_id}
          onSubmit={handleForwardSuccess}
        />
      )}

      {/* Set Reminder Dialog */}
      {selectedEscalation && (
        <SetReminderDialog
          open={isReminderDialogOpen}
          onOpenChange={handleReminderDialogClose}
          escalationId={parseInt(selectedEscalation.escalation_id)}
          employeeId={selectedEscalation.employee_info.employee_id}
          onSubmit={handleReminderSuccess}
        />
      )}
    </div>
  );
}; 